import { useEffect, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { reportsApi, type BSReport, type CFReport, type CtReport, type PLReport, type ReportsKpi } from '../lib/api/reports';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';

type ReportTab = 'pl' | 'bs' | 'cf' | 'tm' | 'ct';
type Period = 'thang' | 'quy' | 'ytd' | 'nam';

const TAB_LABEL: Record<ReportTab, string> = {
  pl: 'Kết quả kinh doanh',
  bs: 'Cân đối kế toán',
  cf: 'Lưu chuyển tiền tệ',
  tm: 'Thuyết minh BCTC',
  ct: 'Chi phí công tác',
};

const PERIOD_LABEL: Record<Period, string> = {
  thang: 'Tháng 07/2026',
  quy: 'Quý 3/2026',
  ytd: 'Năm 2026 YTD',
  nam: 'Năm 2026',
};

export function ReportsPage() {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const [tab, setTab] = useState<ReportTab>('pl');
  const [period, setPeriod] = useState<Period>('thang');
  const [kpi, setKpi] = useState<ReportsKpi | null>(null);
  const [ctData, setCtData] = useState<CtReport | null>(null);
  const [plData, setPlData] = useState<PLReport | null>(null);
  const [bsData, setBsData] = useState<BSReport | null>(null);
  const [cfData, setCfData] = useState<CFReport | null>(null);
  const isKtt = user?.role === 'KTT';

  useEffect(() => { reportsApi.kpi().then(setKpi).catch(() => {}); }, []);
  useEffect(() => {
    if (tab === 'ct') reportsApi.ct().then(setCtData).catch(() => {});
    if (tab === 'pl') reportsApi.pl(2026).then(setPlData).catch(() => {});
    if (tab === 'bs') reportsApi.bs(2026).then(setBsData).catch(() => {});
    if (tab === 'cf') reportsApi.cf(2026).then(setCfData).catch(() => {});
  }, [tab]);

  return (
    <AppShell title="Báo cáo" subtitle="Báo cáo tài chính · P&L · BS · CF · Thuyết minh BCTC · Công tác phí">
      {/* KPI */}
      <div className="rpt-kpi-grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
        <div className="rpt-kpi blue">
          <div className="dsh-kpi-label">Doanh thu</div>
          <div className="dsh-kpi-value">{kpi ? formatVND(kpi.revenue) : '—'}</div>
          <div className="dsh-kpi-delta">Từ TK loại 5</div>
        </div>
        <div className="rpt-kpi purple">
          <div className="dsh-kpi-label">Tổng chi phí</div>
          <div className="dsh-kpi-value">{kpi ? formatVND(kpi.cost) : '—'}</div>
          <div className="dsh-kpi-delta">TK loại 6+8</div>
        </div>
        <div className="rpt-kpi green">
          <div className="dsh-kpi-label">LN gộp</div>
          <div className="dsh-kpi-value">{kpi ? formatVND(kpi.grossProfit) : '—'}</div>
          <div className="dsh-kpi-delta">Biên {kpi?.grossMargin ?? 0}%</div>
        </div>
        <div className="rpt-kpi blue">
          <div className="dsh-kpi-label">LN trước thuế</div>
          <div className="dsh-kpi-value">{kpi ? formatVND(kpi.preTax) : '—'}</div>
          <div className="dsh-kpi-delta">Biên {kpi?.preTaxMargin ?? 0}%</div>
        </div>
        <div className="rpt-kpi green">
          <div className="dsh-kpi-label">LN sau thuế</div>
          <div className="dsh-kpi-value">{kpi ? formatVND(kpi.afterTax) : '—'}</div>
          <div className="dsh-kpi-delta">Thuế 20%</div>
        </div>
        <div className="rpt-kpi blue">
          <div className="dsh-kpi-label">Dòng tiền</div>
          <div className="dsh-kpi-value">{kpi ? formatVND(kpi.cashFlow) : '—'}</div>
          <div className="dsh-kpi-delta">Simplified</div>
        </div>
      </div>

      <div className="rpt-workspace pay-page-card">
      {/* Report tabs */}
      <div className="apr-tabs">
        {(['pl', 'bs', 'cf', 'tm', 'ct'] as ReportTab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`apr-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {/* Period + actions bar */}
      <div className="rpt-tools pay-page-card">
        <div className="lgr-period-tabs">
          {(['thang', 'quy', 'ytd', 'nam'] as Period[]).map((p) => (
            <button key={p} type="button" className={period === p ? 'active' : ''} onClick={() => setPeriod(p)}>
              {PERIOD_LABEL[p]}
            </button>
          ))}
        </div>
        <div className="pay-toolbar-right">
          <button className="pay-btn-outline" type="button" onClick={() => window.print()}>In báo cáo</button>
          <button className="pay-btn-outline" type="button" onClick={() => showToast(`Đã chuẩn bị xuất Excel ${TAB_LABEL[tab]}`)}>Xuất Excel</button>
        </div>
      </div>

      {tab === 'pl' && <ReportPL data={plData} />}
      {tab === 'bs' && <ReportBS data={bsData} />}
      {tab === 'cf' && <ReportCF data={cfData} />}
      {tab === 'tm' && <ReportTM isKtt={isKtt} onEdit={() => showToast('Modal chỉnh Thuyết minh BCTC — đang phát triển (KTT)')} />}
      {tab === 'ct' && <ReportCT data={ctData} />}
      </div>

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}

/* ============================================================ */

function ReportPL({ data }: { data: PLReport | null }) {
  if (!data) return <div className="pay-empty">Đang tải…</div>;
  const revCur = data.rows[0]?.cur || 1;
  return (
    <div className="dsh-card">
      <table className="pay-tbl rpt-tbl">
        <thead>
          <tr>
            <th>Mã</th>
            <th>Chỉ tiêu</th>
            <th style={{ textAlign: 'right' }}>Tháng {String(data.month).padStart(2, '0')}/{data.year}</th>
            <th style={{ textAlign: 'right' }}>Tháng trước</th>
            <th style={{ textAlign: 'right' }}>Chênh lệch</th>
            <th style={{ textAlign: 'right' }}>% DT</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => {
            const diff = r.cur - r.prev;
            const pct = r.prev !== 0 ? Math.round((diff / Math.abs(r.prev)) * 100) : 0;
            const dtPct = revCur ? Math.round((Math.abs(r.cur) / revCur) * 1000) / 10 : 0;
            const hi = r.code === '60';
            return (
              <tr key={r.code} className={`${r.bold ? 'rpt-row-bold' : ''} ${hi ? 'rpt-row-hi' : ''}`}>
                <td>{r.code}</td>
                <td>{r.label}</td>
                <td style={{ textAlign: 'right', color: r.cur < 0 ? '#b91c1c' : undefined }}>
                  {r.cur < 0 ? `(${formatVND(-r.cur)})` : formatVND(r.cur)}
                </td>
                <td style={{ textAlign: 'right', color: r.prev < 0 ? '#b91c1c' : undefined }}>
                  {r.prev < 0 ? `(${formatVND(-r.prev)})` : formatVND(r.prev)}
                </td>
                <td style={{ textAlign: 'right', color: pct > 0 ? '#15803d' : pct < 0 ? '#b91c1c' : '#94a3b8' }}>
                  {pct > 0 ? '+' : ''}{pct}%
                </td>
                <td style={{ textAlign: 'right' }}>{dtPct}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="rpt-note">TT 200/2014/TT-BTC — chưa qua kiểm toán — thuế TNDN 20%</div>
    </div>
  );
}

function ReportBS({ data }: { data: BSReport | null }) {
  if (!data) return <div className="pay-empty">Đang tải…</div>;
  return (
    <div className="rpt-bs-grid">
      <div className="dsh-card">
        <h3 style={{ margin: '0 0 12px', color: '#1e3a8a' }}>TÀI SẢN</h3>
        <table className="pay-tbl rpt-tbl">
          <tbody>
            {data.assets.map((sec) => (
              <>
                <tr key={sec.code} className="rpt-row-hi"><td colSpan={2}><b>{sec.label}</b></td><td style={{ textAlign: 'right', fontWeight: 700 }}>{formatVND(sec.total)}</td></tr>
                {sec.children.map((c) => (
                  <tr key={c.code}><td>{c.code}</td><td>{c.label}</td><td style={{ textAlign: 'right' }}>{formatVND(c.v)}</td></tr>
                ))}
              </>
            ))}
            <tr className="rpt-row-bold"><td colSpan={2}>Tổng cộng tài sản</td><td style={{ textAlign: 'right' }}>{formatVND(data.totalTS)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="dsh-card">
        <h3 style={{ margin: '0 0 12px', color: '#9d174d' }}>NGUỒN VỐN</h3>
        <table className="pay-tbl rpt-tbl">
          <tbody>
            {data.liabilities.map((sec) => (
              <>
                <tr key={sec.code} className="rpt-row-hi"><td colSpan={2}><b>{sec.label}</b></td><td style={{ textAlign: 'right', fontWeight: 700 }}>{formatVND(sec.total)}</td></tr>
                {sec.children.map((c) => (
                  <tr key={c.code}><td>{c.code}</td><td>{c.label}</td><td style={{ textAlign: 'right' }}>{formatVND(c.v)}</td></tr>
                ))}
              </>
            ))}
            <tr className="rpt-row-bold"><td colSpan={2}>Tổng cộng nguồn vốn</td><td style={{ textAlign: 'right' }}>{formatVND(data.totalNV)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="rpt-balance-note">
        {data.balanced
          ? `✓ Cân đối: Tổng TS = Tổng NV = ${formatVND(data.totalTS)}`
          : `⚠ Lệch cân đối: TS ${formatVND(data.totalTS)} vs NV ${formatVND(data.totalNV)} — chênh ${formatVND(Math.abs(data.totalTS - data.totalNV))}`}
      </div>
    </div>
  );
}

function ReportCF({ data }: { data: CFReport | null }) {
  if (!data) return <div className="pay-empty">Đang tải…</div>;
  let currentSection = '';
  return (
    <div className="dsh-card">
      <table className="pay-tbl rpt-tbl">
        <thead>
          <tr>
            <th style={{ width: 60 }}>Mã</th>
            <th>Chỉ tiêu</th>
            <th style={{ textAlign: 'right' }}>Tháng {String(data.month).padStart(2, '0')}/{data.year}</th>
            <th style={{ textAlign: 'right' }}>Luỹ kế YTD</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => {
            const showSection = r.section && r.section !== currentSection;
            if (showSection) currentSection = r.section;
            return (
              <>
                {showSection && (
                  <tr key={`sec-${r.section}`} className="rpt-row-section">
                    <td colSpan={4}>
                      {r.section === 'I' ? 'I. Lưu chuyển từ HĐ kinh doanh' :
                       r.section === 'II' ? 'II. Lưu chuyển từ HĐ đầu tư' :
                       'III. Lưu chuyển từ HĐ tài chính'}
                    </td>
                  </tr>
                )}
                <tr key={i} className={`${r.bold ? 'rpt-row-bold' : ''}`}>
                  <td>{r.code}</td>
                  <td>{r.label}</td>
                  <td style={{ textAlign: 'right', color: r.cur < 0 ? '#b91c1c' : undefined }}>{r.cur < 0 ? `(${formatVND(-r.cur)})` : formatVND(r.cur)}</td>
                  <td style={{ textAlign: 'right', color: r.ytd < 0 ? '#b91c1c' : undefined }}>{r.ytd < 0 ? `(${formatVND(-r.ytd)})` : formatVND(r.ytd)}</td>
                </tr>
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReportTM({ isKtt, onEdit }: { isKtt: boolean; onEdit: () => void }) {
  const SECTIONS = [
    { num: 'I', title: 'Đặc điểm hoạt động', items: [
      ['1', 'Hình thức sở hữu vốn', 'Công ty TNHH 2 thành viên trở lên'],
      ['2', 'Lĩnh vực kinh doanh', 'CNTT, phát triển phần mềm, Drone Show'],
      ['3', 'Ngành nghề', 'Lập trình, tư vấn CNTT, dịch vụ giải trí'],
    ]},
    { num: 'III', title: 'Chuẩn mực & Chế độ kế toán áp dụng', items: [
      ['1', 'Chế độ kế toán', 'Thông tư 200/2014/TT-BTC'],
      ['2', 'Chuẩn mực', 'VAS — Chuẩn mực kế toán Việt Nam'],
      ['3', 'Đồng tiền hạch toán', 'VND'],
    ]},
    { num: 'IV', title: 'Chính sách kế toán áp dụng', items: [
      ['1', 'Nguyên tắc ghi nhận doanh thu', 'Ghi nhận khi hoàn thành CCDV + KH chấp nhận'],
      ['2', 'Nguyên tắc ghi nhận chi phí', 'Nguyên tắc phù hợp doanh thu (matching)'],
      ['3', 'Nguyên tắc khấu hao TSCĐ', 'Đường thẳng theo TT45/2013/TT-BTC'],
    ]},
  ];
  return (
    <div className="dsh-card">
      <div className="dsh-card-head">
        <div>
          <h3>Thuyết minh Báo cáo Tài chính — Form B09-DN TT200</h3>
          <p className="dsh-card-sub">Năm tài chính 2026 · Cập nhật lần cuối 25/07/2026 bởi Nguyễn Thị Hà (KTT)</p>
        </div>
        {isKtt && (
          <button className="pay-btn-primary" type="button" onClick={onEdit}>Chỉnh sửa</button>
        )}
      </div>

      {SECTIONS.map((s) => (
        <div key={s.num} className="rpt-tm-section">
          <h4>{s.num}. {s.title}</h4>
          <table className="pay-tbl rpt-tbl">
            <tbody>
              {s.items.map(([n, q, a]) => (
                <tr key={n}>
                  <td style={{ width: 40 }}>{n}</td>
                  <td style={{ width: 260 }}>{q}</td>
                  <td>{a}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className="rpt-tm-sign">
        <div><b>Người lập biểu</b><br />Lê Huỳnh Diệu<br /><i>NV Kế toán</i></div>
        <div><b>Kế toán trưởng</b><br />Nguyễn Thị Hà<br /><i>CCKT-2024/0892</i></div>
        <div><b>Giám đốc</b><br />Phạm Minh Đức<br /><i>CEO</i></div>
      </div>
    </div>
  );
}

function ReportCT({ data }: { data: CtReport | null }) {
  const s = data?.summary;
  const trips = data?.trips ?? [];
  return (
    <>
      <div className="dsh-kpi-grid pyr-kpi-grid">
        <div className="dsh-kpi dsh-kpi-blue">
          <div className="dsh-kpi-label">Tổng chi công tác</div>
          <div className="dsh-kpi-value">{s ? formatVND(s.totalAmount) : '—'}</div>
          <div className="dsh-kpi-delta">{s?.totalTrips ?? 0} chuyến</div>
        </div>
        <div className="dsh-kpi dsh-kpi-green">
          <div className="dsh-kpi-label">Trong định mức</div>
          <div className="dsh-kpi-value">{s?.inQuotaCount ?? 0} chuyến</div>
          <div className="dsh-kpi-delta">{s ? formatVND(s.inQuotaAmount) : '—'}</div>
        </div>
        <div className="dsh-kpi dsh-kpi-purple">
          <div className="dsh-kpi-label">Vượt định mức</div>
          <div className="dsh-kpi-value">{s?.overQuotaCount ?? 0} chuyến</div>
          <div className="dsh-kpi-delta">{s ? formatVND(s.overQuotaAmount) : '—'} · CEO ký</div>
        </div>
        <div className="dsh-kpi dsh-kpi-blue">
          <div className="dsh-kpi-label">Chờ quyết toán</div>
          <div className="dsh-kpi-value">{trips.filter((t) => !t.actual).length}</div>
        </div>
      </div>

      <div className="dsh-card">
        <div className="dsh-card-head">
          <h3>Chi tiết công tác phí</h3>
          <span className="dsh-card-sub">Theo NV · So sánh Thực chi vs Định mức QCCT</span>
        </div>
        <table className="pay-tbl">
          <thead>
            <tr>
              <th>Mã ĐNC</th>
              <th>Nhân viên</th>
              <th>Địa điểm</th>
              <th style={{ textAlign: 'right' }}>Ngày</th>
              <th style={{ textAlign: 'right' }}>Tạm ứng</th>
              <th style={{ textAlign: 'right' }}>Thực chi</th>
              <th style={{ textAlign: 'right' }}>Định mức</th>
              <th style={{ textAlign: 'right' }}>Chênh</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {trips.length === 0 && (
              <tr><td colSpan={9} className="pay-empty">Chưa có phiếu công tác nào.</td></tr>
            )}
            {trips.map((t) => {
              const diff = (t.actual ?? 0) - (t.quota ?? 0);
              const statusLabel = t.status === 'DISBURSED' ? 'Đã QT' : t.status === 'PENDING_KTT' ? 'Chờ KTT' : t.status === 'PENDING_CEO' ? 'Chờ CEO' : t.status;
              return (
                <tr key={t.code}>
                  <td><span className="coa-code-pill">{t.code}</span></td>
                  <td><b>{t.name}</b></td>
                  <td>{t.place}</td>
                  <td style={{ textAlign: 'right' }}>{t.days}</td>
                  <td style={{ textAlign: 'right' }}>{formatVND(t.advance)}</td>
                  <td style={{ textAlign: 'right' }}>{t.actual != null ? formatVND(t.actual) : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{t.quota != null ? formatVND(t.quota) : '—'}</td>
                  <td style={{ textAlign: 'right', color: diff > 0 ? '#b91c1c' : diff < 0 ? '#15803d' : '#64748b', fontWeight: 600 }}>
                    {t.actual != null && t.quota != null ? (diff > 0 ? '+' : '') + formatVND(diff) : '—'}
                  </td>
                  <td>
                    <span className={`pyr-status pyr-status-${diff > 0 ? 'red' : 'green'}`}>
                      {statusLabel}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
